import { useState, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { SignaturePad } from "@/components/forms/SignaturePad";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, MapPin, PenTool, Upload, X } from "lucide-react";
import { toast } from "sonner";
import type { FormField } from "@/data/system-forms";

interface FormRendererProps {
  formName: string;
  formDescription?: string;
  fields: FormField[];
  onSubmit: (data: Record<string, any>) => void;
  onSaveDraft?: (data: Record<string, any>) => void;
  initialData?: Record<string, any>;
  readOnly?: boolean;
}

export function FormRenderer({
  formName,
  formDescription,
  fields,
  onSubmit,
  onSaveDraft,
  initialData = {},
  readOnly = false,
}: FormRendererProps) {
  const [formData, setFormData] = useState<Record<string, any>>(initialData);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [uploadingPhotos, setUploadingPhotos] = useState<Record<string, boolean>>({});
  const [capturingLocation, setCapturingLocation] = useState<Record<string, boolean>>({});
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [isInitialized, setIsInitialized] = useState(false);

  // Reinitialize whenever a different form/template is mounted into the renderer
  useEffect(() => {
    if (Object.keys(initialData).length > 0 && !isInitialized) {
      setFormData(initialData);
      setIsInitialized(true);
    } else if (Object.keys(initialData).length === 0 && !isInitialized) {
      setFormData({});
      setIsInitialized(true);
    }
  }, [initialData, isInitialized]);

  const updateField = (fieldId: string, value: any) => {
    setFormData((prev) => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[fieldId];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    fields.forEach((field) => {
      if (field.required && !formData[field.id]) {
        newErrors[field.id] = `${field.label} is required`;
      }

      if (field.validation) {
        const value = formData[field.id];
        if (value !== undefined && value !== null) {
          if (field.validation.min !== undefined && value < field.validation.min) {
            newErrors[field.id] = `Minimum value is ${field.validation.min}`;
          }
          if (field.validation.max !== undefined && value > field.validation.max) {
            newErrors[field.id] = `Maximum value is ${field.validation.max}`;
          }
          if (field.validation.pattern) {
            const regex = new RegExp(field.validation.pattern);
            if (!regex.test(String(value))) {
              newErrors[field.id] = 'Invalid format';
            }
          }
        }
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      onSubmit(formData);
    } else {
      toast.error("Please fix the errors before submitting");
    }
  };

  const handleSaveDraft = () => {
    if (onSaveDraft) {
      onSaveDraft(formData);
    }
  };

  const handlePhotoUpload = async (fieldId: string, file: File) => {
    setUploadingPhotos((prev) => ({ ...prev, [fieldId]: true }));
    
    try {
      // Convert file to base64 for storage
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        const currentPhotos = formData[fieldId] || [];
        updateField(fieldId, [...currentPhotos, { url: base64, name: file.name, uploadedAt: new Date().toISOString() }]);
        toast.success("Photo uploaded successfully");
        setUploadingPhotos((prev) => ({ ...prev, [fieldId]: false }));
      };
      reader.onerror = () => {
        toast.error("Failed to upload photo");
        setUploadingPhotos((prev) => ({ ...prev, [fieldId]: false }));
      };
      reader.readAsDataURL(file);
    } catch (error) {
      toast.error("Failed to upload photo");
      setUploadingPhotos((prev) => ({ ...prev, [fieldId]: false }));
    }
  };

  const handleRemovePhoto = (fieldId: string, index: number) => {
    const currentPhotos = formData[fieldId] || [];
    updateField(fieldId, currentPhotos.filter((_: any, i: number) => i !== index));
    toast.success("Photo removed");
  };

  const handleCaptureLocation = (fieldId: string) => {
    if (!navigator.geolocation) {
      toast.error("Geolocation is not supported by your browser");
      return;
    }

    setCapturingLocation((prev) => ({ ...prev, [fieldId]: true }));

    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy,
          capturedAt: new Date().toISOString(),
        };
        updateField(fieldId, location);
        toast.success("Location captured successfully");
        setCapturingLocation((prev) => ({ ...prev, [fieldId]: false }));
      },
      (error) => {
        let message = "Failed to capture location";
        if (error.code === error.PERMISSION_DENIED) {
          message = "Location permission denied";
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          message = "Location unavailable";
        }
        toast.error(message);
        setCapturingLocation((prev) => ({ ...prev, [fieldId]: false }));
      }
    );
  };

  const shouldShowField = (field: FormField) => {
    if (!field.conditionalLogic) return true;
    
    const { fieldId, operator, value } = field.conditionalLogic.showIf;
    const fieldValue = formData[fieldId];

    switch (operator) {
      case 'equals':
        return fieldValue === value;
      case 'notEquals':
        return fieldValue !== value;
      case 'contains':
        return String(fieldValue).includes(String(value));
      default:
        return true;
    }
  };

  const renderField = (field: FormField) => {
    if (!shouldShowField(field)) return null;

    const hasError = !!errors[field.id];
    const fieldValue = formData[field.id];

    return (
      <div key={field.id} className="space-y-2">
        <Label htmlFor={field.id} className="flex items-center gap-2">
          {field.label}
          {field.required && <span className="text-destructive">*</span>}
        </Label>

        {field.type === 'text' && (
          <Input
            id={field.id}
            placeholder={field.placeholder}
            value={fieldValue || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            disabled={readOnly}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {field.type === 'textarea' && (
          <Textarea
            id={field.id}
            placeholder={field.placeholder}
            value={fieldValue || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            disabled={readOnly}
            className={hasError ? 'border-destructive' : ''}
            rows={4}
          />
        )}

        {field.type === 'number' && (
          <Input
            id={field.id}
            type="number"
            placeholder={field.placeholder}
            value={fieldValue || ''}
            onChange={(e) => updateField(field.id, parseFloat(e.target.value))}
            disabled={readOnly}
            className={hasError ? 'border-destructive' : ''}
            min={field.validation?.min}
            max={field.validation?.max}
          />
        )}

        {field.type === 'date' && (
          <Input
            id={field.id}
            type="date"
            value={fieldValue || ''}
            onChange={(e) => updateField(field.id, e.target.value)}
            disabled={readOnly}
            className={hasError ? 'border-destructive' : ''}
          />
        )}

        {field.type === 'select' && (
          <Select value={fieldValue} onValueChange={(value) => updateField(field.id, value)} disabled={readOnly}>
            <SelectTrigger className={hasError ? 'border-destructive' : ''}>
              <SelectValue placeholder="Select an option" />
            </SelectTrigger>
            <SelectContent>
              {field.options?.map((option) => (
                <SelectItem key={option} value={option}>
                  {option}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {field.type === 'radio' && (
          <RadioGroup value={fieldValue} onValueChange={(value) => updateField(field.id, value)} disabled={readOnly}>
            {field.options?.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <RadioGroupItem value={option} id={`${field.id}-${option}`} />
                <Label htmlFor={`${field.id}-${option}`}>{option}</Label>
              </div>
            ))}
          </RadioGroup>
        )}

        {field.type === 'checkbox' && (!field.options || field.options.length === 0) && (
          <div className="flex items-center space-x-2 rounded-md border border-input px-3 py-2">
            <Checkbox
              id={field.id}
              checked={Boolean(fieldValue)}
              onCheckedChange={(checked) => updateField(field.id, Boolean(checked))}
              disabled={readOnly}
            />
            <Label htmlFor={field.id} className="text-sm font-normal">
              Mark complete
            </Label>
          </div>
        )}

        {field.type === 'checkbox' && field.options && field.options.length > 0 && (
          <div className="space-y-2">
            {field.options?.map((option) => (
              <div key={option} className="flex items-center space-x-2">
                <Checkbox
                  id={`${field.id}-${option}`}
                  checked={fieldValue?.includes(option) || false}
                  onCheckedChange={(checked) => {
                    const currentValue = fieldValue || [];
                    const newValue = checked
                      ? [...currentValue, option]
                      : currentValue.filter((v: string) => v !== option);
                    updateField(field.id, newValue);
                  }}
                  disabled={readOnly}
                />
                <Label htmlFor={`${field.id}-${option}`}>{option}</Label>
              </div>
            ))}
          </div>
        )}

        {field.type === 'photo' && (
          <div className="space-y-4">
            <input
              ref={(el) => (fileInputRefs.current[field.id] = el)}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) {
                  handlePhotoUpload(field.id, file);
                  e.target.value = '';
                }
              }}
              disabled={readOnly}
            />
            
            {(!fieldValue || fieldValue.length === 0) && (
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <Camera className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-2">
                  <div className="text-sm text-muted-foreground">Click to upload photo</div>
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => fileInputRefs.current[field.id]?.click()}
                  disabled={readOnly || uploadingPhotos[field.id]}
                >
                  <Upload className="w-4 h-4 mr-2" />
                  Upload Photo
                </Button>
              </div>
            )}

            {fieldValue && fieldValue.length > 0 && (
              <div className="space-y-2">
                {fieldValue.map((photo: any, index: number) => (
                  <div key={index} className="flex items-center gap-2 p-2 border rounded-lg">
                    <img src={photo.url} alt={photo.name} className="w-16 h-16 object-cover rounded" />
                    <div className="flex-1">
                      <p className="text-sm font-medium">{photo.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {new Date(photo.uploadedAt).toLocaleString()}
                      </p>
                    </div>
                    {!readOnly && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        onClick={() => handleRemovePhoto(field.id, index)}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                ))}
                {!readOnly && (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRefs.current[field.id]?.click()}
                    disabled={uploadingPhotos[field.id]}
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    Add Another Photo
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        {field.type === 'signature' && (
          <SignaturePad
            value={fieldValue || ""}
            onChange={(signature) => updateField(field.id, signature)}
          />
        )}

        {field.type === 'location' && (
          <div className="space-y-4">
            {!fieldValue && (
              <div className="border-2 border-dashed rounded-lg p-8 text-center">
                <MapPin className="w-12 h-12 mx-auto text-muted-foreground mb-2" />
                <p className="text-sm text-muted-foreground mb-2">
                  {capturingLocation[field.id] ? "Capturing location..." : "Capture your current GPS location"}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => handleCaptureLocation(field.id)}
                  disabled={readOnly || capturingLocation[field.id]}
                >
                  <MapPin className="w-4 h-4 mr-2" />
                  Capture Location
                </Button>
              </div>
            )}

            {fieldValue && (
              <div className="p-4 border rounded-lg bg-muted/50">
                <div className="flex items-start justify-between">
                  <div className="space-y-1">
                    <p className="text-sm font-medium">Location Captured</p>
                    <p className="text-xs text-muted-foreground">
                      Lat: {fieldValue.latitude.toFixed(6)}, Lng: {fieldValue.longitude.toFixed(6)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      Accuracy: ±{Math.round(fieldValue.accuracy)}m
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {new Date(fieldValue.capturedAt).toLocaleString()}
                    </p>
                  </div>
                  {!readOnly && (
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => updateField(field.id, null)}
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {hasError && <p className="text-sm text-destructive">{errors[field.id]}</p>}
      </div>
    );
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-start justify-between">
          <div>
            <CardTitle>{formName}</CardTitle>
            {formDescription && <CardDescription>{formDescription}</CardDescription>}
          </div>
          {readOnly && <Badge variant="secondary">Read Only</Badge>}
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          {fields.map((field) => renderField(field))}

          {!readOnly && (
            <div className="flex gap-3 justify-end pt-6 border-t">
              <Button type="button" variant="outline" onClick={handleSaveDraft} disabled={!onSaveDraft}>
                Save Draft
              </Button>
              <Button type="submit">Submit Form</Button>
            </div>
          )}
        </form>
      </CardContent>
    </Card>
  );
}
